/**
 * buildScheduleDraftCanvasModel — ScheduleDraft 多轨时间画布 ViewModel 构建层。
 *
 * 本模块把 ScheduleDraftTrack 与外部临时参考轨投影为 timeline adapter 可消费的
 * tracks/items/timeWindow。轨身份、权限和来源语义均来自后端 Track 实体。
 *
 * 设计文档 → docs/20_workspace_ux/20_multitrack_view.md
 * 骨架关联 → lib/canvas/scheduleDraftCanvasHelpers.js (画布纯工具), lib/trackUtils.js (轨 key/航班), hooks/useScheduleDraftEditorData.js (模型消费)
 */

import { debugScheduleDraftTimeline } from "../timelineDebug";
import {
    getAgentAircraftReg,
    getAgentEvaluationSummary,
    getAgentStatusLabel,
    getAgentStatusTone,
    getAgentStrategyLabel,
    getAgentTrackDisplayLabel,
    getAgentTrackStatus,
    isAgentTrack,
    isAgentTrackRunning,
} from "../agentTrackUtils";
import { DIFF_STATUS_META } from "./scheduleDraftCanvasConstants";
import {
    buildDemandReferences,
    buildFlightLookup,
    buildItemTimeLabels,
    buildItemTimeTooltipLines,
    buildStatusBadges,
    buildTrackContext,
    countDiffStatuses,
    getFlightDiffStatus,
    getInitialExpandedTrackIds,
    getTimeWindowFromFlights,
    normalizeString,
} from "./scheduleDraftCanvasHelpers";
import {
    applyReferenceDateShift,
    buildScheduleTrackKey,
    buildTrackDeleteAction,
    formatReferenceSourceTypeLabel,
    formatTrackTypeLabel,
    getDraftFlights,
    getEditingTracks,
    getReferenceDisplayTracks,
    isExecutionReferenceSource,
    getTrackDisplayName,
    getTrackFlightCount,
    getTrackFlights,
    isScheduleTrackKey,
    parseTrackKey,
    toArray,
} from "../trackUtils";
import { getFlightTimeDisplayInfo } from "../flightTimingUtils";
import { AIRCRAFT_AVAILABILITY_CONTEXT_PERIOD_MARGIN_DAYS, buildDraftPeriodTimeWindow } from "../aircraftAvailabilityDisplayPolicy";
import {
    applyTimelineSelection,
    buildSelectedAirportCurfewTimelineItems,
    buildTimelineVisualItems,
    getTransportDemandDestinationAirports,
} from "./timelineVisualSemantics";

export { getInitialExpandedTrackIds };

const DELETED_CHANGE_STATUS = "DELETED";
const AIRCRAFT_AVAILABILITY_CONTEXT_TYPE = "aircraft-availability";
const AIRCRAFT_AVAILABILITY_ITEM_LABEL_MAX_LENGTH = 32;
const AIRCRAFT_AVAILABILITY_TYPE_META = {
    SCHEDULED_MAINTENANCE: { label: "计划维修", tone: "scheduled-maintenance" },
    UNSCHEDULED_MAINTENANCE: { label: "非计划维修", tone: "unscheduled-maintenance" },
    MODIFICATION: { label: "改装", tone: "modification" },
    LEASE_RETURN: { label: "租约归还", tone: "lease-return" },
    RESERVATION: { label: "预留", tone: "reservation" },
    OTHER: { label: "其他不可用", tone: "other" },
};

function isDeletedFlight(flight) {
    return flight?.is_deleted || String(flight?.change_status || "").toUpperCase() === DELETED_CHANGE_STATUS;
}

function getVisibleDraftFlights(flights = []) {
    return toArray(flights).filter((flight) => !isDeletedFlight(flight));
}

function getReferenceSourceFlights(referenceSource) {
    return applyReferenceDateShift(toArray(referenceSource?.flights).filter((flight) => !flight?.is_deleted), referenceSource);
}

function getSortTime(value) {
    const timestamp = value ? new Date(value).getTime() : NaN;
    return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
}

function sortFlightsByDisplayTime(flights = [], useEffectiveTime = false) {
    return [...flights].sort((left, right) => {
        const leftTimeInfo = getFlightTimeDisplayInfo(left, { useEffectiveTime });
        const rightTimeInfo = getFlightTimeDisplayInfo(right, { useEffectiveTime });
        return getSortTime(leftTimeInfo.departureTime || leftTimeInfo.arrivalTime) - getSortTime(rightTimeInfo.departureTime || rightTimeInfo.arrivalTime);
    });
}

function buildTrackReferenceSource(trackKey, track) {
    if (!isAgentTrack(track)) {
        return {
            key: trackKey,
            type: track.track_type,
            label: getTrackDisplayName(track),
            sourceLabel: track.name || formatTrackTypeLabel(track.track_type, "参考轨"),
            context: buildTrackContext("reference", track),
            track,
            flights: sortFlightsByDisplayTime(applyReferenceDateShift(getTrackFlights(track), track), isExecutionReferenceSource(track)),
            usesEffectiveTime: isExecutionReferenceSource(track),
            badges: [{ text: formatTrackTypeLabel(track.track_type, "参考轨"), tone: "kind-track" }],
        };
    }

    const status = getAgentTrackStatus(track);
    const aircraftReg = getAgentAircraftReg(track);
    const strategyLabel = getAgentStrategyLabel(track);
    const evaluationSummary = getAgentEvaluationSummary(track);
    const running = isAgentTrackRunning(track);

    return {
        key: trackKey,
        type: "AGENT",
        label: `AI 方案 · ${getAgentTrackDisplayLabel(track)}`,
        sourceLabel: `${aircraftReg || "Agent"} · ${strategyLabel}`,
        subtitle: `${aircraftReg || "未指定飞机"} · ${strategyLabel} · ${evaluationSummary}`,
        context: running ? "Agent 正在编排，占位方案轨会在完成后自动回填航班。" : `Agent 方案轨，评价：${evaluationSummary}`,
        track,
        flights: getTrackFlights(track),
        badges: [
            { text: "Agent", tone: "kind-agent" },
            { text: strategyLabel, tone: "kind-strategy" },
            { text: getAgentStatusLabel(status), tone: getAgentStatusTone(status) },
        ],
    };
}

function buildReferenceSources(draft, visibleReferenceTrackIds = [], externalReferenceTracks = [], referenceTrackKeys = []) {
    const tracksById = new Map(getReferenceDisplayTracks(draft).map((track) => [String(track.id), track]));
    const externalByKey = new Map(toArray(externalReferenceTracks).map((referenceSource) => [referenceSource.trackKey, referenceSource]));
    const orderedKeys = referenceTrackKeys.length ? referenceTrackKeys : visibleReferenceTrackIds.map(buildScheduleTrackKey);

    return orderedKeys
        .map((trackKey) => {
            if (isScheduleTrackKey(trackKey)) {
                const track = tracksById.get(parseTrackKey(trackKey).id);
                if (!track) {
                    return null;
                }
                return buildTrackReferenceSource(trackKey, track);
            }

            const referenceSource = externalByKey.get(trackKey);
            if (!referenceSource) {
                return null;
            }

            return {
                key: trackKey,
                type: referenceSource.source_type,
                label: referenceSource.source_label || referenceSource.reference_id,
                sourceLabel: referenceSource.source_label || referenceSource.reference_id,
                context: isExecutionReferenceSource(referenceSource)
                    ? "外部执行航班参考轨，时间定位使用 ETD/ETA 优先；ATD/ATA 不参与编排。删除该轨只会从当前工作台移除挂载，不会修改源数据。"
                    : "外部来源只读参考轨。删除该轨只会从当前工作台移除挂载，不会修改源数据。",
                referenceSource,
                flights: sortFlightsByDisplayTime(getReferenceSourceFlights(referenceSource), isExecutionReferenceSource(referenceSource)),
                usesEffectiveTime: isExecutionReferenceSource(referenceSource),
                badges: [{ text: formatReferenceSourceTypeLabel(referenceSource.source_type, "外部来源"), tone: "kind-track" }],
            };
        })
        .filter(Boolean);
}

function resolveEditingTrackForBaseline(draft, editingTracks, currentEditingTrackId) {
    if (!editingTracks.length) {
        return null;
    }

    if (currentEditingTrackId) {
        const currentTrack = editingTracks.find((track) => String(track.id) === String(currentEditingTrackId));
        if (currentTrack) {
            return currentTrack;
        }
    }

    return editingTracks.find((track) => String(track.id) === String(draft?.default_track_id)) || editingTracks[0];
}

function resolvePrimaryReferenceSource(referenceSources, editingTrack) {
    const comparedReferenceTrackId = editingTrack?.compared_reference_track_id;
    if (comparedReferenceTrackId) {
        const boundReferenceSource = referenceSources.find((referenceSource) => String(referenceSource?.track?.id) === String(comparedReferenceTrackId));
        if (boundReferenceSource) {
            return boundReferenceSource;
        }
    }

    return null;
}

function buildReferenceSourceByTrackId(referenceSources, draft) {
    const sourceByTrackId = new Map(
        referenceSources
            .filter((referenceSource) => referenceSource?.track?.id)
            .map((referenceSource) => [String(referenceSource.track.id), referenceSource]),
    );

    getReferenceDisplayTracks(draft).forEach((track) => {
        const trackId = String(track.id);
        if (!sourceByTrackId.has(trackId)) {
            sourceByTrackId.set(trackId, buildTrackReferenceSource(buildScheduleTrackKey(track.id), track));
        }
    });

    return sourceByTrackId;
}

function resolveEditingTrackReferenceSource(referenceSourceByTrackId, editingTrack) {
    const comparedReferenceTrackId = editingTrack?.compared_reference_track_id;
    return comparedReferenceTrackId ? referenceSourceByTrackId.get(String(comparedReferenceTrackId)) || null : null;
}

function buildCanvasTrack({ id, kind, role, label, subtitle, context, sourceLabel, badges, statusBadges, action }) {
    return {
        id,
        kind,
        role,
        label,
        subtitle,
        context,
        sourceLabel,
        badges,
        statusBadges,
        action,
    };
}

function buildFlightItem({ id, trackId, flight, diffStatus, variant, sourceLabel, scheduleTrack, referenceSource, useEffectiveTime = false }) {
    const timeInfo = getFlightTimeDisplayInfo(flight, { useEffectiveTime });
    const timeLabels = buildItemTimeLabels(flight, { useEffectiveTime });
    return {
        id,
        trackId,
        title: normalizeString(flight.flight_number, "未命名航班"),
        subtitle: timeLabels.departure,
        departureLabel: timeLabels.departure,
        arrivalLabel: timeLabels.arrival,
        start: timeInfo.departureTime,
        end: timeInfo.arrivalTime || timeInfo.departureTime,
        variant,
        diffStatus,
        badges: [{ text: DIFF_STATUS_META[diffStatus].label, tone: DIFF_STATUS_META[diffStatus].tone }],
        meta: {
            flight,
            scheduleTrack,
            trackKind: variant,
            sourceLabel,
            referenceSource,
            usesEffectiveTime: useEffectiveTime,
            timeInfo,
            timeTooltipLines: buildItemTimeTooltipLines(flight, { useEffectiveTime }),
        },
    };
}

function normalizeAircraftRegistration(value) {
    return normalizeString(value).trim().toUpperCase();
}

function getAircraftScopeRegistration(value) {
    if (value && typeof value === "object") {
        return normalizeAircraftRegistration(value.registration || value.aircraft_reg || value.aircraftReg || value.reg);
    }
    return normalizeAircraftRegistration(value);
}

function getAircraftScopeRegistrations(draft) {
    return toArray(draft?.aircraft_scope).map(getAircraftScopeRegistration).filter(Boolean);
}

function normalizeAvailabilityWindowType(value) {
    return String(value || "OTHER").trim().toUpperCase();
}

function getAvailabilityWindowTypeMeta(windowType) {
    return AIRCRAFT_AVAILABILITY_TYPE_META[normalizeAvailabilityWindowType(windowType)] || AIRCRAFT_AVAILABILITY_TYPE_META.OTHER;
}

function normalizeAvailabilityWindowsByRegistration(windowsByRegistration = {}) {
    const normalized = new Map();
    Object.entries(windowsByRegistration || {}).forEach(([registration, windows]) => {
        const normalizedRegistration = normalizeAircraftRegistration(registration);
        if (!normalizedRegistration) {
            return;
        }
        normalized.set(normalizedRegistration, toArray(windows));
    });
    return normalized;
}

function parseTimelineTime(value) {
    const time = value ? new Date(value).getTime() : NaN;
    return Number.isFinite(time) ? time : null;
}

function availabilityWindowIntersectsTimeWindow(availabilityWindow, timeWindow) {
    const windowStart = parseTimelineTime(availabilityWindow?.start_time);
    const queryStart = parseTimelineTime(timeWindow?.start);
    const queryEnd = parseTimelineTime(timeWindow?.end);
    if (windowStart === null || queryStart === null || queryEnd === null || queryEnd <= queryStart) {
        return false;
    }

    const windowEnd = parseTimelineTime(availabilityWindow?.end_time) ?? queryEnd;
    return windowStart < queryEnd && windowEnd > queryStart;
}

function getAvailabilityWindowTitle(availabilityWindow) {
    const fullLabel = getAvailabilityWindowDisplayLabel(availabilityWindow);
    if (fullLabel.length <= AIRCRAFT_AVAILABILITY_ITEM_LABEL_MAX_LENGTH) {
        return fullLabel;
    }
    return `${fullLabel.slice(0, AIRCRAFT_AVAILABILITY_ITEM_LABEL_MAX_LENGTH - 1)}…`;
}

function getAvailabilityWindowDisplayLabel(availabilityWindow) {
    const typeMeta = getAvailabilityWindowTypeMeta(availabilityWindow?.window_type);
    const reason = normalizeString(
        availabilityWindow?.title || availabilityWindow?.reason || availabilityWindow?.remarks || typeMeta.label,
        "飞机不可用",
    );
    const airportCode = normalizeString(availabilityWindow?.airport_code);
    if (!airportCode || reason === airportCode || reason.startsWith(`${airportCode} · `)) {
        return reason;
    }
    return [airportCode, reason].filter(Boolean).join(" · ") || reason;
}

function getAvailabilityWindowSubtitle(availabilityWindow) {
    const typeMeta = getAvailabilityWindowTypeMeta(availabilityWindow?.window_type);
    return [typeMeta.label, availabilityWindow?.airport_code].filter(Boolean).join(" @ ");
}

function buildAircraftAvailabilityContextModel({ flightItems = [], registrations = [], windowsByRegistration = {}, timeWindow } = {}) {
    const windowsByReg = normalizeAvailabilityWindowsByRegistration(windowsByRegistration);
    if (!windowsByReg.size || !timeWindow?.start || !timeWindow?.end) {
        return { lanes: [], items: [] };
    }

    const visibleRegistrations = [];
    const seenRegistrations = new Set();
    toArray(registrations).forEach((value) => {
        const registration = normalizeAircraftRegistration(value);
        if (!registration || registration === "UNASSIGNED" || seenRegistrations.has(registration)) {
            return;
        }
        seenRegistrations.add(registration);
        visibleRegistrations.push(registration);
    });
    flightItems.forEach((item) => {
        const registration = normalizeAircraftRegistration(item?.meta?.flight?.aircraft_reg ?? item?.meta?.flight?.aircraftReg);
        if (!registration || registration === "UNASSIGNED" || seenRegistrations.has(registration)) {
            return;
        }
        seenRegistrations.add(registration);
        visibleRegistrations.push(registration);
    });

    const lanes = [];
    const contextItems = [];
    visibleRegistrations.forEach((registration) => {
        const windows = toArray(windowsByReg.get(registration))
            .filter((availabilityWindow) => availabilityWindowIntersectsTimeWindow(availabilityWindow, timeWindow))
            .sort((left, right) => (parseTimelineTime(left?.start_time) ?? 0) - (parseTimelineTime(right?.start_time) ?? 0));
        if (!windows.length) {
            return;
        }

        const laneId = `context:${AIRCRAFT_AVAILABILITY_CONTEXT_TYPE}:${registration}`;
        lanes.push(buildCanvasTrack({
            id: laneId,
            kind: "context",
            role: "context",
            label: `可用性 · ${registration}`,
            subtitle: `${windows.length} 个不可用窗口`,
            context: "飞机主数据实时不可用窗口，只读参考，不参与轨操作或发布快照。",
            sourceLabel: "飞机可用性",
            badges: [{ text: "飞机可用性", tone: "kind-context" }],
            action: null,
        }));

        windows.forEach((availabilityWindow, index) => {
            const typeMeta = getAvailabilityWindowTypeMeta(availabilityWindow?.window_type);
            const displayLabel = getAvailabilityWindowDisplayLabel(availabilityWindow);
            const openEnded = !availabilityWindow?.end_time;
            contextItems.push({
                id: `${laneId}:${availabilityWindow?.id || index}`,
                trackId: laneId,
                itemType: AIRCRAFT_AVAILABILITY_CONTEXT_TYPE,
                title: getAvailabilityWindowTitle(availabilityWindow),
                subtitle: getAvailabilityWindowSubtitle(availabilityWindow),
                start: availabilityWindow?.start_time,
                end: availabilityWindow?.end_time || timeWindow.end,
                variant: "context",
                selectable: true,
                className: `schedule-draft-timeline-aircraft-availability-${typeMeta.tone}`,
                meta: {
                    itemType: AIRCRAFT_AVAILABILITY_CONTEXT_TYPE,
                    contextType: AIRCRAFT_AVAILABILITY_CONTEXT_TYPE,
                    laneType: AIRCRAFT_AVAILABILITY_CONTEXT_TYPE,
                    aircraftReg: registration,
                    availabilityWindow,
                    fullDisplayLabel: displayLabel,
                    windowTypeLabel: typeMeta.label,
                    windowTone: typeMeta.tone,
                    openEnded,
                },
            });
        });
    });

    return { lanes, items: contextItems };
}

function isUsableTimeWindow(timeWindow) {
    const start = timeWindow?.start ? new Date(timeWindow.start).getTime() : NaN;
    const end = timeWindow?.end ? new Date(timeWindow.end).getTime() : NaN;
    return Number.isFinite(start) && Number.isFinite(end) && end > start;
}

function getAvailabilityContextTimeWindow(draft, focusedTimeWindow, fallbackTimeWindow) {
    return buildDraftPeriodTimeWindow(draft, { marginDays: AIRCRAFT_AVAILABILITY_CONTEXT_PERIOD_MARGIN_DAYS }) || focusedTimeWindow || fallbackTimeWindow;
}

/**
 * 构建 ScheduleDraft 时间画布模型。
 * @param {Object|null} draft - ScheduleDraft 详情。
 * @param {Object} config - 画布配置。
 * @param {boolean} config.showEditableTracks - 是否显示当前编辑轨。
 * @param {boolean} config.showDemandReferences - 是否显示需求参考区。
 * @param {Array<string|number>} config.visibleReferenceTrackIds - 已展开参考轨 ID。
 * @param {Array<string>} config.referenceTrackKeys - 用户排序后的参考轨 key。
 * @param {Array<Object>} config.externalReferenceTracks - 已挂载外部参考轨。
 * @param {string|number} config.currentEditingTrackId - 当前编辑轨 ID。
 * @param {{start: string, end: string}=} config.timeWindow - 外部焦点时间窗口。
 * @param {string=} config.selectedItemId - 当前选中画布项 ID。
 * @param {Object=} config.airportConstraintsMap - 机场约束 lookup。
 * @param {Object=} config.aircraftAvailabilityWindowsByRegistration - aircraft_reg -> availability windows。
 * @param {boolean=} config.canDeleteTrack - 当前用户是否具备删除/移除轨权限。
 * @param {boolean=} config.isAgentWorking - Draft 是否有 Agent 正在执行。
 * @returns {Object} timeline adapter 可消费的画布 ViewModel。
 */
export function buildScheduleDraftCanvasModel(draft, config = {}) {
    const showEditableTracks = config.showEditableTracks !== false;
    const showDemandReferences = config.showDemandReferences !== false;
    const visibleReferenceTrackIds = toArray(config.visibleReferenceTrackIds);
    const referenceTrackKeys = toArray(config.referenceTrackKeys);
    const externalReferenceTracks = toArray(config.externalReferenceTracks);
    const currentEditingTrackId = config.currentEditingTrackId;
    const focusedTimeWindow = isUsableTimeWindow(config.timeWindow) ? config.timeWindow : null;
    const selectedItemId = config.selectedItemId || null;
    const airportConstraintsMap = config.airportConstraintsMap || {};
    const aircraftAvailabilityWindowsByRegistration = config.aircraftAvailabilityWindowsByRegistration || {};
    const trackDeleteOptions = {
        canDeleteTrack: Boolean(config.canDeleteTrack),
        isAgentWorking: Boolean(config.isAgentWorking),
    };

    const editingTracks = getEditingTracks(draft);
    const fallbackFlights = getVisibleDraftFlights(getDraftFlights(draft));
    const referenceSources = buildReferenceSources(draft, visibleReferenceTrackIds, externalReferenceTracks, referenceTrackKeys);
    const referenceSourceByTrackId = buildReferenceSourceByTrackId(referenceSources, draft);
    const baselineEditingTrack = resolveEditingTrackForBaseline(draft, editingTracks, currentEditingTrackId);
    const primaryReferenceSource = resolvePrimaryReferenceSource(referenceSources, baselineEditingTrack);
    const primaryReferenceTrack = primaryReferenceSource?.track || null;
    const currentFlights = editingTracks.length ? editingTracks.flatMap((track) => getVisibleDraftFlights(getTrackFlights(track))) : fallbackFlights;
    const currentFlightLookup = buildFlightLookup(currentFlights);

    const editingTimelineTracks = [];
    const referenceTimelineTracks = [];
    const items = [];

    if (showEditableTracks) {
        const visibleEditingTracks = editingTracks.length
            ? editingTracks
            : fallbackFlights.length
              ? [{ id: "current", name: "当前编辑轨", track_type: "EDITING", is_editable: true, flights: fallbackFlights }]
              : [];

        visibleEditingTracks.forEach((track) => {
            const trackFlights = getVisibleDraftFlights(getTrackFlights(track));
            const trackId = `editing:${track.id}`;
            const boundReferenceSource = resolveEditingTrackReferenceSource(referenceSourceByTrackId, track);
            const boundReferenceLookup = buildFlightLookup(boundReferenceSource?.flights || []);
            const counts = countDiffStatuses(trackFlights, boundReferenceLookup, "editable");

            editingTimelineTracks.push(buildCanvasTrack({
                id: trackId,
                kind: "editable",
                role: "editing",
                label: getTrackDisplayName(track),
                subtitle: `${trackFlights.length} 个航班`,
                context: buildTrackContext("editable", track),
                badges: [{ text: formatTrackTypeLabel(track.track_type, "编辑轨"), tone: "kind-editable" }],
                statusBadges: buildStatusBadges(counts),
                action: String(track.id) === "current" ? null : buildTrackDeleteAction(draft, track, trackDeleteOptions),
            }));

            trackFlights.forEach((flight) => {
                const diffStatus = getFlightDiffStatus(flight, boundReferenceLookup, "editable");
                items.push(buildFlightItem({
                    id: `draft:${draft?.id}:${flight.id}`,
                    trackId,
                    flight,
                    diffStatus,
                    variant: "editable",
                    sourceLabel: track.name || draft?.name || "当前计划",
                    scheduleTrack: track,
                }));
            });
        });
    }

    referenceSources.forEach((referenceSource) => {
        const trackId = referenceSource.key;
        const flights = referenceSource.flights || [];
        const counts = countDiffStatuses(flights, currentFlightLookup, "reference");

        referenceTimelineTracks.push(buildCanvasTrack({
            id: trackId,
            kind: "reference",
            role: "reference",
            label: referenceSource.label,
            subtitle: referenceSource.subtitle || `${referenceSource.track ? getTrackFlightCount(referenceSource.track) : flights.length} 个航班`,
            context: referenceSource.context,
            sourceLabel: referenceSource.sourceLabel,
            badges: [
                ...referenceSource.badges,
                referenceSource === primaryReferenceSource ? { text: "主执行基线", tone: "kind-baseline" } : null,
            ].filter(Boolean),
            statusBadges: buildStatusBadges(counts),
            action: referenceSource.track ? buildTrackDeleteAction(draft, referenceSource.track, trackDeleteOptions) : null,
        }));

        flights.forEach((flight, index) => {
            const diffStatus = getFlightDiffStatus(flight, currentFlightLookup, "reference");
            items.push(buildFlightItem({
                id: `${referenceSource.key}:${flight.id || index}`,
                trackId,
                flight,
                diffStatus,
                variant: "reference",
                sourceLabel: referenceSource.label,
                scheduleTrack: referenceSource.track,
                referenceSource,
                useEffectiveTime: referenceSource.usesEffectiveTime,
            }));
        });
    });

    const tracks = [...referenceTimelineTracks, ...editingTimelineTracks];
    const baseTimelineItems = buildTimelineVisualItems(items, {
        demandDestinationAirports: getTransportDemandDestinationAirports(draft),
        airportConstraintsMap,
    });
    const timeWindowFlights = baseTimelineItems.map((item) => ({ std: item.start, sta: item.end }));
    const timeWindow = focusedTimeWindow || getTimeWindowFromFlights(timeWindowFlights, draft);
    const availabilityContextTimeWindow = getAvailabilityContextTimeWindow(draft, focusedTimeWindow, timeWindow);
    const curfewBackgroundItems = buildSelectedAirportCurfewTimelineItems({
        items: baseTimelineItems,
        selectedItemId,
        airportConstraintsMap,
        timeWindow,
    });
    const availabilityContext = buildAircraftAvailabilityContextModel({
        flightItems: items,
        registrations: getAircraftScopeRegistrations(draft),
        windowsByRegistration: aircraftAvailabilityWindowsByRegistration,
        timeWindow: availabilityContextTimeWindow,
    });
    const contextLanes = availabilityContext.lanes;
    const timelineItems = applyTimelineSelection([
        ...baseTimelineItems,
        ...curfewBackgroundItems,
        ...availabilityContext.items,
    ], selectedItemId);
    const visualGroupCount = tracks.length + contextLanes.length;

    const emptyState = visualGroupCount
        ? null
        : {
              title: "当前没有可展示轨道",
              description: showEditableTracks || visibleReferenceTrackIds.length ? "请检查数据是否为空，或尝试重新刷新工作区。" : "编辑轨和参考轨当前都被隐藏了。可在右侧轨配置面板中重新打开需要观察的轨道。",
          };

    const model = {
        tracks,
        contextLanes,
        items: timelineItems,
        timeWindow,
        availabilityContextTimeWindow,
        emptyState,
        demandReferences: buildDemandReferences(draft, showDemandReferences),
        editableTrackCount: tracks.filter((track) => track.kind === "editable").length,
        referenceTrackCount: tracks.filter((track) => track.kind === "reference").length,
        contextLaneCount: contextLanes.length,
        primaryReferenceSource,
        primaryReferenceTrack,
    };

    debugScheduleDraftTimeline("canvas-model", {
        draftId: draft?.id || null,
        trackCount: model.tracks.length,
        contextLaneCount: model.contextLanes.length,
        itemCount: model.items.length,
        primaryReferenceTrack: primaryReferenceTrack ? primaryReferenceTrack.name || primaryReferenceTrack.id : primaryReferenceSource?.label || null,
        timeWindow: model.timeWindow,
        availabilityContextTimeWindow: model.availabilityContextTimeWindow,
        firstItems: model.items.slice(0, 3).map((item) => ({
            id: item.id,
            trackId: item.trackId,
            start: item.start,
            end: item.end,
            title: item.title,
        })),
    });

    return model;
}

export default buildScheduleDraftCanvasModel;
